import { FC, RefObject, useCallback } from 'react';

import { useRecoilState } from 'recoil';

import { GiftOrderDeliveryMode } from '@shared/api/giftOrder/giftOrder.interface';
import Modal from '@shared/components/Modal';
import { TextField } from '@shared/components/TextField';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import type { Address } from 'react-daum-postcode';
import DaumPostcode from 'react-daum-postcode';

const ADDRESS_MODAL_ID = 'giftDeliveryAddressSearch';

interface IProps {
  deliveryMode: GiftOrderDeliveryMode;
  onDeliveryModeChange: (mode: GiftOrderDeliveryMode) => void;
  receiverAddress: string;
  onReceiverAddressChange: (address: string) => void;
  receiverPostalCode: string;
  onReceiverPostalCodeChange: (postalCode: string) => void;
  receiverAddressDetail: string;
  onReceiverAddressDetailChange: (detail: string) => void;
  /** 개별배송(Individual) 선택 허용 여부. false면 일괄배송 전용으로 개별 전달 옵션을 숨김 */
  allowIndividual?: boolean;
  /** 주소 검색 버튼 ref — 배송지 미입력 alert 시 해당 위치로 스크롤 */
  addressButtonRef?: RefObject<HTMLButtonElement>;
}

const GiftDeliveryModeSelector: FC<IProps> = ({
  deliveryMode,
  onDeliveryModeChange,
  receiverAddress,
  onReceiverAddressChange,
  receiverPostalCode,
  onReceiverPostalCodeChange,
  receiverAddressDetail,
  onReceiverAddressDetailChange,
  allowIndividual = true,
  addressButtonRef,
}) => {
  const [, setAddressModalOpen] = useRecoilState(simpleOpenAtomFamily(ADDRESS_MODAL_ID));

  const handleAddressComplete = useCallback(
    (data: Address) => {
      const fullAddress =
        data.address + (data.buildingName ? ` (${data.buildingName})` : '');
      onReceiverAddressChange(fullAddress);
      onReceiverPostalCodeChange(data.zonecode);
      setAddressModalOpen(false);
      history.back();
    },
    [onReceiverAddressChange, onReceiverPostalCodeChange, setAddressModalOpen],
  );

  return (
    <div className="bg-white p-4">
      <h3 className="mb-3 font-bold">배송 상품 수령 방식</h3>

      {allowIndividual ? (
        // 개별/일괄 둘 다 가능 → 선택형 라디오
        <div className="space-y-2">
          {/* 직접 수령 (Bulk) */}
          <label
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
              deliveryMode === GiftOrderDeliveryMode.Bulk
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              name="deliveryMode"
              checked={deliveryMode === GiftOrderDeliveryMode.Bulk}
              onChange={() => onDeliveryModeChange(GiftOrderDeliveryMode.Bulk)}
              className="mt-0.5 accent-red-500"
            />
            <div className="flex-1">
              <p className="text-sm font-bold">직접 수령 (일괄배송)</p>
              <p className="mt-0.5 text-xs text-gray-500">
                입력한 배송지로 전체 수량이 배송됩니다.
              </p>
            </div>
          </label>

          {/* 개별 전달 (Individual) */}
          <label
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
              deliveryMode === GiftOrderDeliveryMode.Individual
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              name="deliveryMode"
              checked={deliveryMode === GiftOrderDeliveryMode.Individual}
              onChange={() => onDeliveryModeChange(GiftOrderDeliveryMode.Individual)}
              className="mt-0.5 accent-red-500"
            />
            <div className="flex-1">
              <p className="text-sm font-bold">개별 전달 (링크 전달)</p>
              <p className="mt-0.5 break-keep text-xs text-gray-500">
                결제 후 수령인에게 링크를 보내면, 수령인이 직접 배송지를 입력합니다.
              </p>
            </div>
          </label>
        </div>
      ) : (
        // 일괄배송 전용 → 선택지 없이 단일 안내
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm font-bold">직접 수령 (일괄배송)</p>
          <p className="mt-0.5 break-keep text-xs text-gray-500">
            입력하신 배송지로 전체 수량이 일괄배송됩니다.
          </p>
        </div>
      )}

      {/* Bulk 선택 시 배송지 입력 폼 */}
      {deliveryMode === GiftOrderDeliveryMode.Bulk && (
        <div className="mt-4 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <h4 className="text-sm font-bold text-gray-700">배송지 정보</h4>

          {/* 주소 검색 */}
          <button
            ref={addressButtonRef}
            type="button"
            onClick={() => setAddressModalOpen(true)}
            className="flex w-full items-center justify-between rounded border border-gray-300 bg-white px-3 py-2.5 text-left text-sm"
          >
            {receiverAddress ? (
              <span className="text-gray-900">
                ({receiverPostalCode}) {receiverAddress}
              </span>
            ) : (
              <span className="text-gray-400">주소 검색</span>
            )}
            <svg
              className="h-4 w-4 flex-shrink-0 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>

          {/* 상세주소 */}
          <TextField
            scrollOnFocus={true}
            className="w-full"
            placeholder="상세주소 입력"
            value={receiverAddressDetail}
            onChange={(e) => onReceiverAddressDetailChange(e.target.value)}
          />

          <p className="text-xs text-gray-400">
            * 수령인 정보는 상단 주문자 정보가 사용됩니다.
          </p>
        </div>
      )}

      {/* 쿠폰형 상품 안내 */}
      <p className="mt-3 text-xs text-gray-400">
        * 쿠폰형 상품은 결제 후 온라인으로 전달할 수 있습니다.
      </p>

      {/* 주소 검색 모달 */}
      <Modal openId={ADDRESS_MODAL_ID}>
        <div className="p-2">
          <h3 className="mb-2 text-center font-bold">주소 검색</h3>
          <DaumPostcode style={{ height: 480 }} onComplete={handleAddressComplete} />
        </div>
      </Modal>
    </div>
  );
};

export default GiftDeliveryModeSelector;
